import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const tealDark = '#0F766E'
const plum = '#1E1040'

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

if (typeof document !== 'undefined' && !document.getElementById('merqlog-place-ac-style')) {
  const style = document.createElement('style')
  style.id = 'merqlog-place-ac-style'
  style.textContent = `
    gmp-place-autocomplete.merqlog-place-ac {
      width: 100%;
      display: block;
    }
    .gmp-place-autocomplete-list,
    gmp-place-autocomplete-list,
    [data-gmp-place-autocomplete-list] {
      z-index: 3000 !important;
    }
  `
  document.head.appendChild(style)
}

let googleMapsLoadingPromise = null

async function loadGoogleMaps() {
  if (window.google?.maps?.importLibrary) {
    await window.google.maps.importLibrary('places')
    return window.google
  }

  if (googleMapsLoadingPromise) return googleMapsLoadingPromise

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    const onReady = async () => {
      try {
        await window.google.maps.importLibrary('places')
        resolve(window.google)
      } catch (err) {
        googleMapsLoadingPromise = null
        reject(err)
      }
    }

    if (existingScript) {
      if (window.google?.maps?.importLibrary) onReady()
      else {
        existingScript.addEventListener('load', onReady)
        existingScript.addEventListener('error', () => {
          googleMapsLoadingPromise = null
          reject(new Error('Failed to load Google Maps'))
        })
      }
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&v=weekly&loading=async&libraries=places&callback=__merqLogMapsReady`
    script.async = true
    script.defer = true
    window.__merqLogMapsReady = () => { onReady().catch(reject) }
    script.onerror = () => {
      googleMapsLoadingPromise = null
      reject(new Error('Failed to load Google Maps script'))
    }
    document.head.appendChild(script)
  })
  return googleMapsLoadingPromise
}

/**
 * TripFormModal
 * Props:
 *   existingTrip: optional — if present, edit that trip. Otherwise create new.
 *   onClose, onSaved(trip)
 */
export default function TripFormModal({ existingTrip = null, onClose, onSaved }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [vehicles, setVehicles] = useState([])
  const [savedAddresses, setSavedAddresses] = useState([])
  const [mileageRateCents, setMileageRateCents] = useState(70)

  // Form state
  const [fromAddress, setFromAddress] = useState(existingTrip?.from_address || '')
  const [toAddress, setToAddress] = useState(existingTrip?.to_address || '')
  const [tripDate, setTripDate] = useState(existingTrip?.trip_date || new Date().toISOString().slice(0, 10))
  const [vehicleId, setVehicleId] = useState(existingTrip?.vehicle_id || '')
  const [purpose, setPurpose] = useState(existingTrip?.purpose || '')
  const [isRoundTrip, setIsRoundTrip] = useState(existingTrip?.round_trip ?? true)
  const [miles, setMiles] = useState(existingTrip?.miles != null ? String(existingTrip.miles) : '')
  const [calculating, setCalculating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [fromPlaceId, setFromPlaceId] = useState(existingTrip?.from_place_id || null)
  const [toPlaceId, setToPlaceId] = useState(existingTrip?.to_place_id || null)
  const [fromConfirmed, setFromConfirmed] = useState(!!existingTrip?.from_address)
  const [toConfirmed, setToConfirmed] = useState(!!existingTrip?.to_address)

  const fromContainerRef = useRef(null)
  const toContainerRef = useRef(null)

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  useEffect(() => {
    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loading) return

    let fromEl = null
    let toEl = null
    let mounted = true

    const attach = async () => {
      try {
        await loadGoogleMaps()
      } catch (err) {
        console.error('Google Maps failed to load:', err)
        setError(t('trip_form.error_maps_load'))
        return
      }

      if (!mounted) return
      if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
        console.error('PlaceAutocompleteElement not available')
        setError(t('trip_form.error_autocomplete'))
        return
      }

      const makeAutocomplete = (initialText) => {
        const el = new window.google.maps.places.PlaceAutocompleteElement({
          includedRegionCodes: ['us'],
        })
        el.classList.add('merqlog-place-ac')
        if (initialText) {
          try { el.value = initialText } catch (_) { /* ignore if read-only */ }
        }
        return el
      }

      if (fromContainerRef.current) {
        fromEl = makeAutocomplete(fromAddress)
        fromEl.addEventListener('gmp-select', async (evt) => {
          try {
            const place = evt.placePrediction.toPlace()
            await place.fetchFields({ fields: ['formattedAddress', 'id'] })
            setFromAddress(place.formattedAddress || '')
            setFromPlaceId(place.id || null)
            setFromConfirmed(true)
          } catch (err) {
            console.error('From place fetch failed:', err)
          }
        })
        fromContainerRef.current.innerHTML = ''
        fromContainerRef.current.appendChild(fromEl)
      }

      if (toContainerRef.current) {
        toEl = makeAutocomplete(toAddress)
        toEl.addEventListener('gmp-select', async (evt) => {
          try {
            const place = evt.placePrediction.toPlace()
            await place.fetchFields({ fields: ['formattedAddress', 'id'] })
            setToAddress(place.formattedAddress || '')
            setToPlaceId(place.id || null)
            setToConfirmed(true)
          } catch (err) {
            console.error('To place fetch failed:', err)
          }
        })
        toContainerRef.current.innerHTML = ''
        toContainerRef.current.appendChild(toEl)
      }

      const handleFocusIn = (ev) => {
        const target = ev.target
        if (!target) return
        const isAddressInput =
          target.tagName === 'INPUT' &&
          (fromContainerRef.current?.contains(target) || toContainerRef.current?.contains(target))
        if (!isAddressInput) return
        if (window.innerWidth > 700) return
        setTimeout(() => {
          target.scrollIntoView({ block: 'start', behavior: 'smooth' })
        }, 250)
      }
      document.addEventListener('focusin', handleFocusIn)

      return () => {
        document.removeEventListener('focusin', handleFocusIn)
      }
    }

    attach()

    return () => {
      mounted = false
      if (fromEl?.remove) fromEl.remove()
      if (toEl?.remove) toEl.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const loadInitialData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const [{ data: profile }, { data: veh }, { data: addrs }] = await Promise.all([
        supabase.from('profiles').select('mileage_rate_cents').eq('id', user.id).single(),
        supabase.from('vehicles').select('*').eq('user_id', user.id).order('is_default', { ascending: false }),
        supabase.from('saved_addresses').select('*').eq('user_id', user.id).order('use_count', { ascending: false }),
      ])

      setMileageRateCents(profile?.mileage_rate_cents ?? 70)
      setVehicles(veh || [])
      setSavedAddresses(addrs || [])

      if (!vehicleId && veh?.length) {
        const def = veh.find(v => v.is_default) || veh[0]
        setVehicleId(def.id)
      }
    } catch (err) {
      console.error('Load error:', err)
    }
    setLoading(false)
  }

  const calculateMiles = useCallback(async () => {
    if (!fromAddress || !toAddress) return

    setCalculating(true)
    setError('')

    try {
      await loadGoogleMaps()

      const service = new window.google.maps.DistanceMatrixService()
      const response = await new Promise((resolve, reject) => {
        service.getDistanceMatrix({
          origins: fromPlaceId ? [{ placeId: fromPlaceId }] : [fromAddress],
          destinations: toPlaceId ? [{ placeId: toPlaceId }] : [toAddress],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        }, (resp, status) => {
          if (status === 'OK') resolve(resp)
          else reject(new Error(status))
        })
      })

      const element = response?.rows?.[0]?.elements?.[0]
      if (element?.status !== 'OK' || !element?.distance?.value) {
        setError(t('trip_form.error_no_route'))
        setCalculating(false)
        return
      }

      const meters = element.distance.value
      const milesValue = meters / 1609.344
      setMiles(milesValue.toFixed(1))
      setError('')
    } catch (err) {
      console.error('Distance calculation failed:', err)
      setError(err.message || t('trip_form.error_calc_failed'))
    } finally {
      setCalculating(false)
    }
  }, [fromAddress, toAddress, fromPlaceId, toPlaceId, t])

  useEffect(() => {
    if (!fromConfirmed || !toConfirmed) return
    if (!fromAddress || !toAddress) return
    calculateMiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromConfirmed, toConfirmed, fromAddress, toAddress])

  const bumpAddressUsage = async (addressString) => {
    if (!addressString) return
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const existing = savedAddresses.find(a => a.address?.toLowerCase() === addressString.toLowerCase())
    if (existing) {
      await supabase
        .from('saved_addresses')
        .update({ use_count: (existing.use_count || 1) + 1 })
        .eq('id', existing.id)
    } else {
      await supabase
        .from('saved_addresses')
        .insert({
          user_id: user.id,
          label: addressString.split(',')[0].trim().slice(0, 50),
          address: addressString,
          use_count: 1,
        })
    }
  }

  const pickSavedAddress = (addr, target) => {
    if (target === 'from') {
      setFromAddress(addr.address)
      setFromPlaceId(null)
      setFromConfirmed(true)
    } else {
      setToAddress(addr.address)
      setToPlaceId(null)
      setToConfirmed(true)
    }
  }

  const handleSave = async () => {
    if (!fromAddress || !toAddress) {
      setError(t('trip_form.error_addresses_required'))
      return
    }
    if (!miles || parseFloat(miles) <= 0) {
      setError(t('trip_form.error_miles_required'))
      return
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error(t('trip_form.error_not_signed_in'))

      const baseMiles = parseFloat(miles)
      const effectiveMiles = isRoundTrip ? baseMiles * 2 : baseMiles
      const selectedVehicle = vehicles.find(v => v.id === vehicleId)
      const effectiveRateCents = selectedVehicle?.rate_cents_override != null
        ? selectedVehicle.rate_cents_override
        : mileageRateCents
      const deductionAmount = effectiveMiles * (effectiveRateCents / 100)

      const payload = {
        user_id: user.id,
        trip_date: tripDate,
        from_address: fromAddress,
        to_address: toAddress,
        miles: effectiveMiles,
        round_trip: isRoundTrip,
        purpose: purpose || null,
        vehicle_id: vehicleId || null,
        rate_used_cents: effectiveRateCents,
        deduction_amount: deductionAmount,
      }

      let result
      if (existingTrip) {
        const { data, error: err } = await supabase
          .from('mileage_trips')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', existingTrip.id)
          .select()
          .single()
        if (err) throw err
        result = data
      } else {
        const { data, error: err } = await supabase
          .from('mileage_trips')
          .insert(payload)
          .select()
          .single()
        if (err) throw err
        result = data
      }

      bumpAddressUsage(fromAddress)
      bumpAddressUsage(toAddress)

      onSaved?.(result)
      onClose()
    } catch (err) {
      console.error('Save trip error:', err)
      setError(err.message || t('trip_form.error_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  const formatCurrency = (n) => {
    const lang = i18n.language || 'en'
    const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(n || 0)
  }

  const selectedVehiclePreview = vehicles.find(v => v.id === vehicleId)
  const effectiveDisplayRateCents = selectedVehiclePreview?.rate_cents_override != null
    ? selectedVehiclePreview.rate_cents_override
    : mileageRateCents
  const baseMilesNum = parseFloat(miles) || 0
  const effectiveMiles = isRoundTrip ? baseMilesNum * 2 : baseMilesNum
  const previewDeduction = effectiveMiles * (effectiveDisplayRateCents / 100)

  const frequentAddresses = savedAddresses.filter(a => (a.use_count || 0) >= 2).slice(0, 4)

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.title}>{existingTrip ? t('trip_form.edit_title') : t('trip_form.add_title')}</div>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {loading ? (
          <div style={styles.loading}>{t('common.loading')}</div>
        ) : (
          <>
            {/* FROM address */}
            <div style={styles.field}>
              <label style={styles.label}>{t('trip_form.from_label')} *</label>
              <div
                ref={fromContainerRef}
                style={styles.acContainer}
                data-existing-address={fromAddress}
              />
              {frequentAddresses.length > 0 && (
                <div style={styles.chips}>
                  <span style={styles.chipLabel}>{t('trip_form.quick_fill')}</span>
                  {frequentAddresses.map(addr => (
                    <button
                      key={`from-${addr.id}`}
                      type="button"
                      style={styles.chip}
                      onClick={() => pickSavedAddress(addr, 'from')}
                    >
                      {addr.label || addr.address.split(',')[0]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* TO address */}
            <div style={styles.field}>
              <label style={styles.label}>{t('trip_form.to_label')} *</label>
              <div
                ref={toContainerRef}
                style={styles.acContainer}
                data-existing-address={toAddress}
              />
              {frequentAddresses.length > 0 && (
                <div style={styles.chips}>
                  <span style={styles.chipLabel}>{t('trip_form.quick_fill')}</span>
                  {frequentAddresses.map(addr => (
                    <button
                      key={`to-${addr.id}`}
                      type="button"
                      style={styles.chip}
                      onClick={() => pickSavedAddress(addr, 'to')}
                    >
                      {addr.label || addr.address.split(',')[0]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Distance preview */}
            <div style={styles.distanceBox}>
              {calculating ? (
                <div style={styles.distanceRow}>
                  <span style={styles.distanceLabel}>{t('trip_form.calculating')}</span>
                </div>
              ) : miles ? (
                <>
                  <div style={styles.distanceRow}>
                    <span style={styles.distanceLabel}>
                      {isRoundTrip ? t('trip_form.round_trip') : t('trip_form.one_way')}
                    </span>
                    <span style={styles.distanceValue}>{t('trip_form.miles_value', { miles: effectiveMiles.toFixed(1) })}</span>
                  </div>
                  <div style={styles.distanceRow}>
                    <span style={styles.distanceLabel}>{t('trip_form.deduction_at_rate', { rate: (effectiveDisplayRateCents / 100).toFixed(2) })}</span>
                    <span style={styles.deductionValue}>{formatCurrency(previewDeduction)}</span>
                  </div>
                </>
              ) : (
                <div style={styles.distanceRow}>
                  <span style={styles.distanceLabel}>{t('trip_form.enter_addresses_hint')}</span>
                </div>
              )}
            </div>

            {/* Manual miles override */}
            <div style={styles.field}>
              <label style={styles.label}>
                {t('trip_form.miles_label')}
                {calculating ? ` · ${t('trip_form.calculating_short')}` : ''}
              </label>
              <input
                type="number"
                step="0.1"
                style={styles.input}
                placeholder="0.0"
                value={miles}
                onChange={(e) => setMiles(e.target.value)}
              />
            </div>

            {/* Round trip */}
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={isRoundTrip}
                onChange={(e) => setIsRoundTrip(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxLabel}>{t('trip_form.round_trip_checkbox')}</span>
            </label>

            {/* Date + Vehicle */}
            <div style={styles.fieldRow}>
              <div style={styles.field}>
                <label style={styles.label}>{t('trip_form.date_label')} *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={tripDate}
                  onChange={(e) => setTripDate(e.target.value)}
                />
              </div>
              {vehicles.length > 0 && (
                <div style={styles.field}>
                  <label style={styles.label}>{t('trip_form.vehicle_label')}</label>
                  <select
                    style={styles.input}
                    value={vehicleId}
                    onChange={(e) => setVehicleId(e.target.value)}
                  >
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.icon || '🚗'} {v.label || t('trip_form.vehicle_default_name')}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>{t('trip_form.purpose_label')}</label>
              <input
                type="text"
                style={styles.input}
                placeholder={t('trip_form.purpose_placeholder')}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button style={styles.btnSecondary} onClick={onClose}>{t('common.cancel')}</button>
              <button
                style={{ ...styles.btnPrimary, opacity: (!fromAddress || !toAddress || !miles || saving) ? 0.5 : 1 }}
                onClick={handleSave}
                disabled={!fromAddress || !toAddress || !miles || saving}
              >
                {saving ? t('common.saving') : existingTrip ? t('trip_form.save_changes') : t('trip_form.save_trip')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '20px', padding: '24px',
    width: '100%', maxWidth: '520px', maxHeight: '92vh',
    overflowY: 'visible',
    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  title: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: plum, letterSpacing: '-0.3px' },
  closeBtn: {
    background: '#F3F4F6', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px',
    fontSize: '14px', color: '#6B7280', cursor: 'pointer', fontWeight: 700,
  },
  loading: { padding: '40px', textAlign: 'center', color: '#9CA3AF' },
  field: { marginBottom: '14px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  label: {
    display: 'block', fontSize: '12px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  acContainer: {
    width: '100%',
    minHeight: '42px',
    display: 'block',
    colorScheme: 'light',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', alignItems: 'center' },
  chipLabel: {
    fontSize: '11px', color: '#9CA3AF', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: '4px',
  },
  chip: {
    background: '#F9FAFB', border: '1px solid #E5E7EB',
    borderRadius: '100px', padding: '5px 12px',
    fontSize: '12px', fontWeight: 600, color: plum,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  distanceBox: {
    background: `linear-gradient(135deg, ${tealDark}, ${teal})`,
    color: '#fff', borderRadius: '12px',
    padding: '14px 18px', marginBottom: '14px',
  },
  distanceRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '3px 0', fontSize: '14px',
  },
  distanceLabel: { color: 'rgba(255,255,255,0.9)', fontWeight: 500 },
  distanceValue: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 800, color: '#fff' },
  deductionValue: { fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 800, color: '#fff' },
  checkboxRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 14px', background: '#F9FAFB',
    border: '1px solid #E5E7EB', borderRadius: '10px',
    cursor: 'pointer', marginBottom: '14px',
  },
  checkbox: { accentColor: teal, width: '18px', height: '18px', cursor: 'pointer' },
  checkboxLabel: { fontSize: '14px', fontWeight: 600, color: plum },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '10px 14px', borderRadius: '8px',
    fontSize: '13px', marginTop: '12px', marginBottom: '12px', fontWeight: 500,
  },
  actions: { display: 'flex', gap: '10px', marginTop: '4px' },
  btnPrimary: {
    flex: 2, padding: '13px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnSecondary: {
    flex: 1, padding: '13px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
}
